/**
 * MagicLinkLoginModal — passwordless sign-in by e-mail.
 *
 *   1) E-mail : the user enters their address → POST /api/auth/magic-link/request.
 *   2) Code   : the e-mail carries a one-click link (opens the admin web app) AND
 *               a 6-digit code for the same sign-in; in the app the user types
 *               the code → POST /api/auth/magic-link/verify.
 *
 * The verify response is exactly the password-login payload, so it is handed
 * straight back to LoginScreen: `{ user, token }` → `onAuthenticated` (the
 * screen's `continueAfterLogin`), `{ requires2FA, preAuthToken }` → `on2FA`
 * (the regular TwoFactorChallengeModal). Visual shell mirrors
 * TwoFactorChallengeModal (same OTP input, brand mark and card).
 */
import React, { useEffect, useState } from 'react';
import {
    ActivityIndicator,
    Modal,
    ScrollView,
    StyleSheet,
    Text,
    TextInput,
    TouchableOpacity,
    View,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import axios from 'axios';
import { ApiConfig } from '../config/ApiConfig';
import { getDeviceTrustToken, getOrCreateDeviceTrustId } from '../helpers/storage';
import { ModalKeyboardAvoider } from './KeyboardAware';
import { COLORS, OtpInput, TwoFactorBrand } from './twoFactor/TwoFactorUI';

const MAGIC_LINK_BASE = `${ApiConfig.apiURL}/api/auth/magic-link`;
const RESEND_COOLDOWN = 45;
const REQUEST_TIMEOUT_MS = 20000;
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Map a failed magic-link request/verify to a user-facing message.
 *
 * @param {*} err - axios rejection.
 * @param {Function} t - i18next translator.
 * @param {('request'|'verify')} phase - Which call failed.
 * @returns {string}
 */
export const magicLinkErrorMessage = (err, t, phase) => {
    if (err?.code === 'ECONNABORTED' || !err?.response) {
        return t('login_network_error', { defaultValue: 'Cannot reach the server. Check your internet connection and try again.' });
    }
    const status = err.response.status;
    const data = err.response.data || {};

    if (status === 429) {
        // Request: per-address cap. Verify: per-IP throttle (a spent code budget
        // deliberately answers the generic 400, so it can't reveal accounts).
        return t('forgot_pw_too_many_attempts', { defaultValue: 'Too many requests. Please try again later.' });
    }
    if (status === 423 || data.code === 'ACCOUNT_LOCKED') {
        return data.indefinite
            ? t('login_locked_indefinite', { defaultValue: 'Your account has been locked for security. Reset your password using "Forgotten password" or contact an administrator to regain access.' })
            : t('login_locked_generic', { defaultValue: 'Too many failed attempts. Your account is temporarily locked. Please try again later.' });
    }
    if (status === 502) {
        return t('forgot_pw_email_failed', { defaultValue: 'Could not send the email. Please try again shortly.' });
    }
    if (status === 403 && data.code === 'ACCESS_REVOKED') {
        return t('access_revoked_message', { defaultValue: 'System usage rights revoked. Please contact your administrator.' });
    }
    if (status === 403 && data.code === 'ACCOUNT_INACTIVE') {
        return t('account_inactive_message', { defaultValue: 'Your account is no longer active. Please contact your administrator.' });
    }
    if (status === 424) {
        return data.message || t('magic_link_failed', { defaultValue: 'Could not sign you in. Please try again.' });
    }
    if (phase === 'verify') {
        return t('magic_link_code_invalid', { defaultValue: 'The code is invalid or has expired.' });
    }

    return t('magic_link_request_failed', { defaultValue: 'Could not send the sign-in link. Please try again.' });
};

const MagicLinkLoginModal = ({
    visible,
    initialEmail = '',
    t,
    deviceHeaders = {},
    onClose,
    onAuthenticated,
    on2FA,
}) => {
    const [step, setStep] = useState('email'); // email | code
    const [email, setEmail] = useState('');
    const [hint, setHint] = useState('');
    const [code, setCode] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [cooldown, setCooldown] = useState(0);

    // Fresh state every time the sheet opens; pre-fill a remembered e-mail.
    useEffect(() => {
        if (!visible) {
            return;
        }
        setStep('email');
        setEmail(EMAIL_PATTERN.test(initialEmail || '') ? initialEmail : '');
        setHint('');
        setCode('');
        setError('');
        setBusy(false);
    }, [visible, initialEmail]);

    useEffect(() => {
        if (cooldown <= 0) {
            return undefined;
        }
        const id = setInterval(() => setCooldown((c) => Math.max(0, c - 1)), 1000);

        return () => clearInterval(id);
    }, [cooldown]);

    const requestLink = async () => {
        const value = email.trim().toLowerCase();

        setError('');
        if (!EMAIL_PATTERN.test(value)) {
            setError(t('magic_link_email_invalid', { defaultValue: 'Please enter a valid email address.' }));

            return;
        }
        setBusy(true);
        try {
            const { data } = await axios.post(`${MAGIC_LINK_BASE}/request`, { email: value }, { timeout: REQUEST_TIMEOUT_MS });

            setHint(data?.hint || value);
            setCode('');
            setStep('code');
            setCooldown(RESEND_COOLDOWN);
        } catch (err) {
            setError(magicLinkErrorMessage(err, t, 'request'));
        } finally {
            setBusy(false);
        }
    };

    const verify = async (override) => {
        const digits = String(override ?? code).replace(/\D/g, '');

        setError('');
        if (digits.length !== 6) {
            setError(t('forgot_pw_enter_full_code', { defaultValue: 'Enter the full 6-digit code.' }));

            return;
        }
        setBusy(true);
        try {
            const [deviceTrustToken, deviceId] = await Promise.all([
                getDeviceTrustToken().catch(() => null),
                getOrCreateDeviceTrustId().catch(() => null),
            ]);
            const { data } = await axios.post(
                `${MAGIC_LINK_BASE}/verify`,
                {
                    email: email.trim().toLowerCase(),
                    code: digits,
                    deviceTrustToken: deviceTrustToken || undefined,
                    deviceId: deviceId || undefined,
                },
                {
                    timeout: REQUEST_TIMEOUT_MS,
                    headers: deviceHeaders,
                },
            );

            if (data?.requires2FA && data?.preAuthToken) {
                onClose?.();
                on2FA?.(data);

                return;
            }
            if (data?.token && data?.user?._id) {
                onClose?.();
                await onAuthenticated?.(data);

                return;
            }
            setError(t('magic_link_failed', { defaultValue: 'Could not sign you in. Please try again.' }));
        } catch (err) {
            setError(magicLinkErrorMessage(err, t, 'verify'));
        } finally {
            setBusy(false);
        }
    };

    return (
        <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose}>
            <ModalKeyboardAvoider style={styles.overlay} justify="center">
                <ScrollView
                    style={styles.flex}
                    contentContainerStyle={styles.scroll}
                    keyboardShouldPersistTaps="handled"
                    keyboardDismissMode="interactive"
                    showsVerticalScrollIndicator={false}
                >
                    <View style={styles.card}>
                        <View style={styles.header}>
                            <TwoFactorBrand icon={step === 'email' ? 'mail-outline' : 'mail-open-outline'} />
                            <Text style={styles.eyebrow}>{t('magic_link_eyebrow', { defaultValue: 'Passwordless sign-in' })}</Text>
                            <Text style={styles.title}>
                                {step === 'email'
                                    ? t('magic_link_title', { defaultValue: 'Sign in with an email link' })
                                    : t('magic_link_check_inbox', { defaultValue: 'Check your inbox' })}
                            </Text>
                            <Text style={styles.subtitle}>
                                {step === 'email'
                                    ? t('magic_link_subtitle', { defaultValue: 'No password needed — we will email you a one-time sign-in code.' })
                                    : t('magic_link_code_subtitle', { hint, defaultValue: 'We sent a sign-in email to {{hint}}. Enter the 6-digit code from it below.' })}
                            </Text>
                        </View>

                        {step === 'email' ? (
                            <TextInput
                                style={styles.input}
                                value={email}
                                onChangeText={(text) => {
                                    setEmail(text);
                                    setError('');
                                }}
                                placeholder={t('magic_link_email_placeholder', { defaultValue: 'Email address' })}
                                placeholderTextColor={COLORS.sub}
                                autoCapitalize="none"
                                autoCorrect={false}
                                autoComplete="email"
                                textContentType="emailAddress"
                                keyboardType="email-address"
                                returnKeyType="send"
                                onSubmitEditing={requestLink}
                                editable={!busy}
                                autoFocus
                            />
                        ) : (
                            <View style={styles.codeArea}>
                                <OtpInput
                                    value={code}
                                    onChange={(v) => {
                                        setCode(v);
                                        setError('');
                                    }}
                                    onComplete={(full) => verify(full)}
                                    length={6}
                                    disabled={busy}
                                />
                                <View style={styles.resendRow}>
                                    {cooldown > 0 ? (
                                        <Text style={styles.muted}>
                                            {t('magic_link_resend_in', { count: cooldown, defaultValue: 'Resend email in {{count}}s' })}
                                        </Text>
                                    ) : (
                                        <TouchableOpacity onPress={requestLink} disabled={busy}>
                                            <Text style={styles.link}>{t('magic_link_resend', { defaultValue: 'Resend email' })}</Text>
                                        </TouchableOpacity>
                                    )}
                                </View>
                            </View>
                        )}

                        {!!error && (
                            <View style={styles.errorRow}>
                                <Ionicons name="alert-circle" size={16} color={COLORS.danger} />
                                <Text style={styles.error}>{error}</Text>
                            </View>
                        )}

                        <TouchableOpacity
                            onPress={step === 'email' ? requestLink : () => verify()}
                            disabled={busy}
                            style={[styles.primaryButton, busy ? styles.primaryDisabled : null]}
                        >
                            {busy
                                ? <ActivityIndicator color="#fff" />
                                : (
                                    <Text style={styles.primaryText}>
                                        {step === 'email'
                                            ? t('magic_link_send', { defaultValue: 'Email me a sign-in code' })
                                            : t('magic_link_sign_in', { defaultValue: 'Sign in' })}
                                    </Text>
                                )}
                        </TouchableOpacity>

                        {step === 'code' && (
                            <TouchableOpacity
                                onPress={() => {
                                    setStep('email');
                                    setError('');
                                }}
                                disabled={busy}
                                style={styles.cancelBtn}
                            >
                                <Text style={styles.link}>{t('magic_link_change_email', { defaultValue: 'Use a different email' })}</Text>
                            </TouchableOpacity>
                        )}
                        <TouchableOpacity onPress={onClose} disabled={busy} style={styles.cancelBtn}>
                            <Text style={styles.cancel}>{t('cancel', { defaultValue: 'Cancel' })}</Text>
                        </TouchableOpacity>
                    </View>
                </ScrollView>
            </ModalKeyboardAvoider>
        </Modal>
    );
};

const styles = StyleSheet.create({
    flex: {
        flex: 1,
    },
    overlay: {
        flex: 1,
        backgroundColor: 'rgba(15,23,42,0.55)',
        justifyContent: 'center',
    },
    scroll: {
        flexGrow: 1,
        justifyContent: 'center',
        padding: 18,
    },
    card: {
        width: '100%',
        maxWidth: 440,
        alignSelf: 'center',
        borderRadius: 24,
        backgroundColor: '#fff',
        padding: 22,
        shadowColor: '#0F172A',
        shadowOpacity: 0.18,
        shadowRadius: 30,
        shadowOffset: { width: 0, height: 18 },
        elevation: 12,
    },
    header: {
        alignItems: 'center',
        marginBottom: 18,
    },
    eyebrow: {
        marginTop: 14,
        fontSize: 11,
        fontWeight: '700',
        letterSpacing: 1,
        textTransform: 'uppercase',
        color: COLORS.brandDark,
    },
    title: {
        marginTop: 4,
        fontSize: 22,
        fontWeight: '800',
        color: COLORS.ink,
        textAlign: 'center',
    },
    subtitle: {
        marginTop: 8,
        fontSize: 13,
        lineHeight: 19,
        color: COLORS.sub,
        textAlign: 'center',
    },
    input: {
        borderWidth: 1.5,
        borderColor: COLORS.line,
        borderRadius: 14,
        paddingHorizontal: 14,
        paddingVertical: 13,
        color: COLORS.ink,
        fontSize: 16,
    },
    codeArea: {
        alignItems: 'center',
    },
    resendRow: {
        marginTop: 14,
        alignItems: 'center',
    },
    muted: {
        fontSize: 12,
        color: COLORS.sub,
    },
    link: {
        color: COLORS.brandDark,
        fontWeight: '700',
        fontSize: 14,
        textAlign: 'center',
    },
    errorRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 6,
        marginTop: 12,
    },
    error: {
        color: COLORS.danger,
        fontSize: 13,
        flex: 1,
    },
    primaryButton: {
        marginTop: 18,
        backgroundColor: COLORS.brand,
        paddingVertical: 15,
        borderRadius: 14,
        alignItems: 'center',
    },
    primaryDisabled: {
        opacity: 0.6,
    },
    primaryText: {
        color: '#fff',
        fontWeight: '800',
        fontSize: 15,
    },
    cancelBtn: {
        marginTop: 6,
        paddingVertical: 12,
        alignItems: 'center',
    },
    cancel: {
        color: COLORS.sub,
        fontWeight: '600',
        fontSize: 14,
    },
});

export default MagicLinkLoginModal;
